import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { build_params, download_export } from '@/lib/list-query';
import type { SortState } from '@/hooks/use-url-state';
import type { UserDetail, UserListFilters, UserListResponse } from './types';

/**
 * `build_params` drops empty values and folds the sort token into the two
 * params the API takes. The previous hand-rolled version is how the date filter
 * came to be wired into the query key but not the request — the screen
 * refetched and sent exactly the same query.
 */
async function fetch_user_list(
  filters: UserListFilters,
  sort: SortState,
): Promise<UserListResponse> {
  const res = await api.get<UserListResponse>('/admin/users', {
    params: build_params(filters as Record<string, string | number | undefined>, sort),
  });
  return res.data;
}

async function fetch_user_detail(id: string): Promise<UserDetail> {
  const res = await api.get<UserDetail>(`/admin/users/${id}`);
  return res.data;
}

export function use_user_list(filters: UserListFilters, sort: SortState = null) {
  return useQuery({
    queryKey: ['admin-user-list', filters, sort],
    queryFn: () => fetch_user_list(filters, sort),
    // Keep the previous page on screen while the next one loads, so paging and
    // sorting don't blank the table.
    placeholderData: (prev) => prev,
  });
}

/** Every row matching the current filters, as a CSV. Paging is excluded. */
export function export_users(filters: UserListFilters, sort: SortState): Promise<void> {
  const { page: _page, page_size: _page_size, ...rest } = filters;
  return download_export(
    '/admin/users/export',
    build_params(rest as Record<string, string | number | undefined>, sort),
    'users.csv',
  );
}

/**
 * Block / unblock a customer.
 *
 * The endpoints have existed since the module was written and the read side was
 * already wired (the list shows an "inactive" pill, the detail page an
 * "Active: No" row) — only the write control was missing, so an admin could see
 * that someone was blocked but never do it. Vendors are rejected by the backend
 * with a 422; suspend them from the vendor page instead.
 */
async function set_user_active(id: string, is_active: boolean): Promise<UserDetail> {
  const res = await api.post<UserDetail>(`/admin/users/${id}/${is_active ? 'unblock' : 'block'}`);
  return res.data;
}

export function use_set_user_active(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (is_active: boolean) => set_user_active(id, is_active),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-user-detail', id] });
      void qc.invalidateQueries({ queryKey: ['admin-user-list'] });
    },
  });
}

/**
 * Block / unblock from a list row or the bulk bar, where the id isn't fixed at
 * hook-call time.
 */
export function use_set_user_active_by_id() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      set_user_active(id, is_active),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ['admin-user-detail', id] });
      void qc.invalidateQueries({ queryKey: ['admin-user-list'] });
    },
  });
}

export function use_user_detail(id: string | undefined) {
  return useQuery({
    queryKey: id ? ['admin-user-detail', id] : ['admin-user-detail', 'noop'],
    queryFn: () => fetch_user_detail(id as string),
    enabled: typeof id === 'string' && id.length > 0,
  });
}

/**
 * Counts for the metric strip.
 *
 * `/admin/stats` only knows two things about users — the total and how many are
 * active — which is not enough for a customer module: "never booked" and
 * "dormant" are the numbers an operator acts on. Rather than adding a bespoke
 * stats endpoint per module, each tile asks the list endpoint for one row and
 * reads `total` off the envelope. Cheap (a COUNT plus a one-row page), always
 * consistent with the list it links to, and it means a new tile needs no
 * backend work at all.
 */
export function use_user_counts(base: Pick<UserListFilters, 'role'>) {
  const count_of = async (extra: Partial<UserListFilters>): Promise<number> => {
    const res = await api.get<UserListResponse>('/admin/users', {
      params: build_params({ ...base, ...extra, page_size: 1 } as Record<
        string,
        string | number | undefined
      >),
    });
    return res.data.total;
  };

  const month_start = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  })();

  return useQuery({
    queryKey: ['admin-user-counts', base.role ?? 'all'],
    queryFn: async () => {
      const [total, joined_this_month, never_booked, dormant, blocked, no_address] =
        await Promise.all([
          count_of({}),
          count_of({ from: month_start }),
          count_of({ has_bookings: 'false' }),
          count_of({ dormant_days: 60 }),
          count_of({ is_active: 'false' }),
          count_of({ has_address: 'false' }),
        ]);
      return { total, joined_this_month, never_booked, dormant, blocked, no_address };
    },
    // These sit above a list the operator is actively filtering; a minute-old
    // count is fine, a request per keystroke is not.
    staleTime: 60_000,
  });
}
